import axios from 'axios';
import { IUserData, PlayInput } from 'Types';

/**
 * Reading and saving the account's answer to what the player plays on
 * (`play_input`): a MIDI keyboard, a piano or keyboard the app cannot hear, or
 * nothing yet. The answer has its own save routes; it is never part of the
 * settings write, so an older copy of the account cannot overwrite it.
 */

const baseUrl = `${process.env.REACT_APP_BACKEND_URL}/api/v1/`;

export interface PlayInputOption {
  value: PlayInput;
  title: string;
}

/** The three answers, in the order every picker shows them. */
export const PLAY_INPUT_OPTIONS: readonly PlayInputOption[] = [
  { value: 'midi', title: 'A MIDI keyboard' },
  { value: 'unconnected_piano', title: 'A piano or keyboard without MIDI' },
  { value: 'none', title: 'Nothing yet, just exploring' },
];

/** Where the picker's question is asked after the device screen. */
export const PLAY_INPUT_QUESTION = 'What do you play on?';

/** Said where saving the answer failed; the old answer stays. */
export const SAVE_FAILED_MESSAGE = 'That did not save. Please try again.';

/** The keyboard-buying help, linked from the "Nothing yet" answer. */
export const KEYBOARD_BUYING_FAQ_URL = 'https://museflow.ai/faq';

const isPlayInput = (value: unknown): value is PlayInput =>
  PLAY_INPUT_OPTIONS.some((option) => option.value === value);

/**
 * True when the backend stores the answer: its account record carries the
 * field (null until answered). A backend that does not store it yet leaves the
 * field out, and then the app does not ask.
 */
export const accountStoresPlayInput = (userData: Partial<IUserData> | null | undefined): boolean =>
  userData != null && 'play_input' in userData;

/** The answer as the picker shows it: an unanswered account plays on MIDI. */
export const shownPlayInput = (playInput: PlayInput | null | undefined): PlayInput =>
  playInput ?? 'midi';

/**
 * True when the player finished the welcome screen but has not answered the
 * device question. Only an explicit `false` counts: a backend that does not
 * store the flag leaves it out, and those players are never sent to it.
 */
export const needsDeviceSetup = (userData: Partial<IUserData> | null | undefined): boolean =>
  userData?.welcome_flow_setup_complete === false;

/** Where the app goes once the welcome screen is done. */
export const postWelcomeRoute = (userData: Partial<IUserData> | null | undefined): string =>
  needsDeviceSetup(userData) ? '/device-setup' : '/roadmap';

const savedPlayInput = (response: { data?: Partial<IUserData> }, asked: PlayInput): PlayInput => {
  const stored = response.data?.play_input;
  return isPlayInput(stored) ? stored : asked;
};

/** Save the answer from the picker, a prompt or the blocker. Resolves to the saved value. */
export const savePlayInput = async (playInput: PlayInput): Promise<PlayInput> => {
  if (!isPlayInput(playInput)) throw new Error(`Not a play input: ${String(playInput)}`);
  const response = await axios.put<Partial<IUserData>>(
    `${baseUrl}user-data/set-play-input?play_input=${playInput}`,
    {},
  );
  return savedPlayInput(response, playInput);
};

/**
 * Save the device screen's answer together with the end of the welcome flow,
 * so the account never holds one without the other. Resolves to the saved value.
 */
export const saveDeviceSetup = async (playInput: PlayInput): Promise<PlayInput> => {
  if (!isPlayInput(playInput)) throw new Error(`Not a play input: ${String(playInput)}`);
  const response = await axios.put<Partial<IUserData>>(
    `${baseUrl}user-data/complete-device-setup?play_input=${playInput}`,
    {},
  );
  return savedPlayInput(response, playInput);
};
